import { Link } from "react-router-dom";
import { ArrowRight, Users, Target, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal } from "../components/Reveal";
import { philosophy, values, stats } from "../mock";

function AboutHero() {
  return (
    <section className="relative pt-44 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/polished-topping.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">About Phoenix</span>
        <h1 className="mt-3 font-display font-black text-white text-4xl md:text-6xl tracking-tight max-w-3xl">Craftsmanship That Rises Above</h1>
        <p className="mt-4 text-zinc-300 text-lg max-w-2xl">Three decades of protecting the region's most demanding facilities with durable, high-performance floor systems.</p>
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section className="py-20 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src="/images/polished-concrete-new.jpg" alt="Phoenix polished concrete facility floor" className="rounded-xl w-full h-[440px] object-cover" />
        </Reveal>
        <Reveal delay={120}>
          <div>
            <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">Our Story</span>
            <h2 className="mt-3 font-display font-extrabold text-white text-3xl md:text-4xl tracking-tight">Our Talented, Creative &amp; Reliable Team</h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">{philosophy.intro}</p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Our talented, experienced and reliable crews have made Phoenix a trusted flooring partner for large-scale
              facilities across the region. When you need a durable, high-performance floor system installed on schedule
              and with minimal disruption to your operations, you're ready to work with Phoenix Decorative Surfaces, Western New York Division.
            </p>
            <Link to="/contact">
              <Button className="mt-7 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                Work With Us <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MissionVision() {
  const cards = [
    { icon: Target, title: "Our Mission", text: philosophy.mission },
    { icon: Eye, title: "Our Vision", text: philosophy.vision },
  ];
  return (
    <section className="py-20 bg-zinc-900/50">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
        {cards.map((item, i) => (
          <Reveal key={item.title} delay={i * 120}>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 h-full">
              <span className="w-12 h-12 rounded-lg bg-orange-500/15 flex items-center justify-center">
                <item.icon className="w-6 h-6 text-orange-400" />
              </span>
              <h3 className="font-display font-bold text-white text-2xl mt-5">{item.title}</h3>
              <p className="text-zinc-400 mt-3 leading-relaxed">{item.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CoreValues() {
  return (
    <section className="py-20 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">What Drives Us</span>
            <h2 className="mt-3 font-display font-extrabold text-white text-3xl md:text-4xl">Our Core Values</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 80}>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 h-full hover:border-orange-500/50 transition-colors">
                <Users className="w-6 h-6 text-orange-400" />
                <h4 className="font-display font-bold text-white text-lg mt-4">{v.title}</h4>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-16 bg-orange-500">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <div className="font-display font-black text-4xl md:text-5xl text-zinc-950">{s.value}</div>
            <div className="text-orange-950/80 text-sm font-medium mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <AboutHero />
      <StorySection />
      <MissionVision />
      <CoreValues />
      <StatsSection />
    </>
  );
}
